import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from "../layout/index";
import axios from 'axios';
import { toast } from "react-toastify";
import { Switch } from 'antd';

const DoctorList = () => {
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL;
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updatingId, setUpdatingId] = useState(null);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        fetchList();
    }, []);
    
    const fetchList = async () => {
        const token = localStorage.getItem("auth_token");
        const tenantId = localStorage.getItem("tenantId");
        
        try {
            const res = await fetch(
                `${API_URL}doctor/list?clinicId=${tenantId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                        tenantId: tenantId,
                    },
                }
            );
            const data = await res.json();
            setDoctors(data);
            setLoading(false);
        } catch (error) {
            console.error("Failed to fetch doctor data:", error);
            toast.error("Failed to fetch doctor data");
            setLoading(false);
        }
    };

    const handleToggleStatus = async (doctor) => {
        if (!doctor?.doctorCode) return;
        const token = localStorage.getItem("auth_token");
        const tenantId = localStorage.getItem("tenantId");
        const API_BASE = process.env.REACT_APP_API_URL;
        const nextActive = !(doctor.userActive === "active");
        try {
            setUpdatingId(doctor.doctorCode);
            
            // First call with tenantId
            const res1 = await fetch(`${API_BASE}doctor/update-passconfig/${doctor.doctorCode}`, {
                method: "PUT",
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                    tenantId: tenantId,
                },
                body: JSON.stringify({ active: nextActive })
            });
            
            if (res1.ok) {
                // Second call without tenantId
                const res2 = await fetch(`${API_BASE}doctor/update-passconfig/${doctor.doctorCode}`, {
                    method: "PUT",
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ active: nextActive })
                });
                
                if (res2.ok) {
                    setDoctors(prev => (prev || []).map(d => d.doctorCode === doctor.doctorCode ? { ...d, userActive: nextActive ? "active" : "inactive" } : d));
                    toast.success(`Doctor ${nextActive ? 'activated' : 'deactivated'}`);
                } else {
                    throw new Error('Second API call failed');
                }
            } else {
                throw new Error('First API call failed');
            }
        } catch (e) {
            console.error(e);
            toast.error('Unable to update doctor status');
        } finally {
            setUpdatingId(null);
        }
    };

    const handleViewDetails = (doctor) => {
        setSelectedDoctor(doctor);
        setShowModal(true);
    };

    const handleEditDoctor = (doctor) => {
        navigate('/doctor-edit', { 
            state: { 
                doctorData: doctor,
                isEdit: true 
            }
        });
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedDoctor(null);
    };

    const formatQualifications = (qualifications) => {
        if (!qualifications || qualifications.length === 0) return '-';
        return qualifications.join(', ');
    };

    const formatSpecialization = (specialization) => {
        if (!specialization || specialization.length === 0) return '-';
        return specialization.join(', ');
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="mt-6 flex justify-center items-center h-64">
                    <div className="text-lg text-[#004276]">Loading doctors...</div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="mt-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
                    <h3 className="text-lg font-semibold text-[#004276]">
                        Doctor List
                    </h3>
                </div>

                {/* Doctor Table */}
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Full Name
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Qualifications
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Specialization
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Medical License Number
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {doctors?.map((doctor) => (
                                    <tr key={doctor.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">
                                               Dr. {doctor.fullName}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">
                                                {formatQualifications(doctor.qualifications)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-900 max-w-xs">
                                                {/* <div className="flex flex-wrap gap-1"> */}
                                                {formatSpecialization(doctor.specialization)}
                                                
                                                {/* </div> */}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">
                                                {doctor.medicalLicenseNumber || '-'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-3">
                                                <span className={`inline-flex items-center gap-2 text-sm font-medium ${doctor.userActive === "active" ? 'text-green-600' : 'text-red-600'}`}>
                                                    {doctor.userActive === "active" ? 'Active' : 'Inactive'}
                                                </span>
                                                <Switch
                                                    checked={doctor.userActive === "active"}
                                                    loading={updatingId === doctor.doctorCode}
                                                    onChange={() => handleToggleStatus(doctor)}
                                                    size="small"
                                                />
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex gap-2">
                                            <button
                                                onClick={() => handleViewDetails(doctor)}
                                                className="rounded-md bg-gradient-to-r from-[#004172] to-[#3cba84] text-white px-4 py-2 text-sm transition-colors duration-200"
                                            >
                                                View
                                            </button>
                                            <button
                                                onClick={() => handleEditDoctor(doctor)}
                                                className="rounded-md bg-gradient-to-r from-[#004172] to-[#3cba84] text-white px-4 py-2 text-sm transition-colors duration-200"
                                            >
                                                Edit
                                            </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Modal for Bank Details and Slot Details */}
                {showModal && selectedDoctor && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                        <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
                            <div className="mt-3">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-medium text-gray-900">
                                        Doctor Details - Dr. {selectedDoctor.fullName} {selectedDoctor.isFamilyDoctor? <span className='inline-block ml-2 text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-medium'>Family Doctor</span> : null}
                                    </h3>
                                    <button
                                        onClick={closeModal}
                                        className="text-gray-400 hover:text-gray-600 text-xl font-bold"
                                    >
                                        ×
                                    </button>
                                </div>

                                {/* Bank Details */}
                                <div className="mb-6">
                                    <h4 className="text-md font-semibold text-[#004276] mb-3 border-b pb-2">
                                        Bank Details
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Bank Name</label>
                                            <p className="text-sm text-gray-900">{selectedDoctor.bankName || '-'}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Account Holder Name</label>
                                            <p className="text-sm text-gray-900">{selectedDoctor.accountHolderName || '-'}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">IFSC Code</label>
                                            <p className="text-sm text-gray-900">{selectedDoctor.ifscCode || '-'}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Account Number</label>
                                            <p className="text-sm text-gray-900">{selectedDoctor.accountNumber || '-'}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Bank Location</label>
                                            <p className="text-sm text-gray-900">{selectedDoctor.bankLoc || '-'}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">PAN Card Number</label>
                                            <p className="text-sm text-gray-900">{selectedDoctor.panCardNumber || '-'}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Slot Details */}
                                <div>
                                    <h4 className="text-md font-semibold text-[#004276] mb-3 border-b pb-2">
                                        Time Slots
                                    </h4>
                                    <div className="space-y-3">
                                        {selectedDoctor.timeSlots && selectedDoctor.timeSlots.length > 0 ? (
                                            selectedDoctor.timeSlots.map((slot, index) => (
                                                <div key={index} className="border rounded-lg p-3 bg-gray-50">
                                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700">Day</label>
                                                            <p className="text-sm text-gray-900">{slot.dayOfWeek}</p>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700">Time</label>
                                                            <p className="text-sm text-gray-900">{slot.startTime} - {slot.endTime}</p>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700">Slot Duration</label>
                                                            <p className="text-sm text-gray-900">{selectedDoctor.slotDuration ? `${selectedDoctor.slotDuration} minutes` : '-'}</p>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700">Week Numbers</label>
                                                            <p className="text-sm text-gray-900">
                                                                {slot.weekNumbers ? slot.weekNumbers.join(', ') : 'All weeks'}
                                                            </p>
                                                        </div>
                                                        {/* <div>
                                                            <label className="block text-sm font-medium text-gray-700">Delay</label>
                                                            <p className="text-sm text-gray-900">{slot.delayByMinutes || 'No delay'}</p>
                                                        </div> */}
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-sm text-gray-500">No time slots available</p>
                                        )}
                                    </div>
                                </div>

                                <div className="mt-6 flex justify-end">
                                    <button
                                        onClick={closeModal}
                                        className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm transition-colors duration-200"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default DoctorList;